import { Info } from "lucide-react";
import { FormProvider } from "react-hook-form";
import { FormInput } from "../../../../components/ui/Input/FormInput/FormInput";
import { FormTextarea } from "../../../../components/ui/Input/FormTextArea/FormTextarea";
import { Card, CardContent, CardHeader } from "../../../../components/ui/common/card";
import { Label } from "../../../../components/ui/common/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "../../../../components/ui/common/select";
import { Switch } from "../../../../components/ui/common/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "../../../../components/ui/common/tooltip";
import { IsRootProduct } from "../../../../config/enums/ProductType.enum";
import ProductImageUpload from "../../shared/components/ProductImageUpload";
import { ProductRegisterViewModel } from "../types/productRegisterView.types";

interface ProductInfoCardProps {
  vm: ProductRegisterViewModel;
}

const EYEBROW_CLASS =
  "tw-text-[10px] tw-uppercase tw-tracking-wider tw-text-muted-foreground tw-font-medium";

/**
 * "PRODUCT INFO" card — Name / Registration Number, Product Type and
 * Average Impact Product, and the image upload. Matches the Lovable
 * prototype's `RegisterProductDialog` field order and controls exactly
 * (Select for Product Type, Switch for Average Impact Product, Additional
 * Information before Product Images) — the real backend only accepts two
 * product types (PROCESS/FARM), unlike the prototype's four, so the Select
 * options are scoped to those.
 */
const ProductInfoCard = ({ vm }: ProductInfoCardProps) => {
  return (
    <Card className="tw-gap-4 tw-p-5">
      <CardHeader className="tw-p-0">
        <span className={EYEBROW_CLASS}>Product info</span>
      </CardHeader>
      <CardContent className="tw-flex tw-flex-col tw-gap-4 tw-p-0">
        <FormProvider {...vm.methods}>
          <div className="tw-grid tw-grid-cols-1 tw-gap-4 sm:tw-grid-cols-3">
            <div className="sm:tw-col-span-2">
              <FormInput
                control={vm.methods.control}
                name="product_name"
                label="Product Name"
                required
                placeholder="e.g. Lovi Beverage"
                errorMessage="Please enter product name"
                className="tw-h-9 tw-text-sm"
                labelClassName="tw-text-xs"
                rules={{
                  validate: (value?: string) =>
                    (value?.length ?? 0) <= 32 || "Maximum 32 characters allowed"
                }}
              />
            </div>
            <FormInput
              control={vm.methods.control}
              name="registration_number"
              label="Register Number"
              placeholder="NP-2026-0001"
              className="tw-h-9 tw-font-mono tw-text-sm"
              labelClassName="tw-text-xs"
              rules={{
                validate: (value?: string) =>
                  !value || value.length <= 32 || "Maximum 32 characters allowed"
              }}
            />
          </div>

          <div className="tw-grid tw-grid-cols-1 tw-gap-4 sm:tw-grid-cols-3">
            <div className="tw-flex tw-flex-col tw-gap-1.5">
              <Label className="tw-text-xs">Product Type</Label>
              <Select value={vm.productType} onValueChange={vm.onProductTypeChange}>
                <SelectTrigger className="!tw-h-9 tw-w-full tw-font-mono tw-text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PROCESS" className="tw-font-mono">
                    PROCESS
                  </SelectItem>
                  <SelectItem value="FARM" className="tw-font-mono">
                    FARM
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {vm.showAverageProductToggle ? (
              <div className="tw-flex tw-flex-col tw-justify-end tw-gap-1.5 sm:tw-col-span-2">
                <TooltipProvider delayDuration={200}>
                  <span className="tw-inline-flex tw-items-center tw-gap-1.5">
                    <Label className="tw-text-xs">Average Impact Product</Label>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="tw-h-3.5 tw-w-3.5 tw-shrink-0 tw-text-primary/70" />
                      </TooltipTrigger>
                      <TooltipContent>
                        Generates an &quot;average product&quot; using the carbon footprint and volume
                        of selected products
                      </TooltipContent>
                    </Tooltip>
                  </span>
                </TooltipProvider>
                <div className="tw-flex tw-h-9 tw-items-center tw-gap-2">
                  <Switch
                    checked={vm.isRootProduct === IsRootProduct.YES}
                    onCheckedChange={checked =>
                      vm.onToggleAverageProduct(checked ? IsRootProduct.YES : IsRootProduct.NO)
                    }
                  />
                  <span className="tw-text-sm tw-text-muted-foreground">
                    {vm.isRootProduct === IsRootProduct.YES ? "Yes" : "No"}
                  </span>
                </div>
              </div>
            ) : null}
          </div>

          <FormTextarea
            control={vm.methods.control}
            name="product_other_info"
            label="Additional Information"
            placeholder="Anything the calculation team should know"
            className="tw-text-sm"
            labelClassName="tw-text-xs"
          />

          <div className="tw-flex tw-flex-col tw-gap-2">
            <div className="tw-flex tw-items-center tw-justify-between">
              <Label className="tw-text-xs">Product Images</Label>
            </div>
            <ProductImageUpload onUpload={vm.onImageSelect} onRemove={() => vm.onImageSelect(null)} />
          </div>
        </FormProvider>
      </CardContent>
    </Card>
  );
};

export default ProductInfoCard;
